import React, { useState, useEffect, useRef } from 'react';
import { Badge } from './badge';
import { Input } from './input';
import { Label } from './label';
import { X, Plus, Search } from 'lucide-react';
import { CompetitorTag } from '@/api/types';

interface CompetitorTagsSelectorProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
}

const CompetitorTagsSelector: React.FC<CompetitorTagsSelectorProps> = ({
  value = [],
  onChange,
  placeholder = "Vyhledejte nebo přidejte konkurenční značky",
  className = "",
  disabled = false,
  label
}) => {
  const [availableTags, setAvailableTags] = useState<CompetitorTag[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load available tags on mount and when search changes
  useEffect(() => {
    const loadTags = async () => {
      if (searchQuery.length < 1) {
        // Load most popular tags when no search
        setIsLoading(true);
        try {
          const response = await fetch('/api/competitor-tags?limit=10');
          const data = await response.json();
          if (data.success) {
            setAvailableTags(data.data);
          }
        } catch (error) {
          console.error('Error loading competitor tags:', error);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // Search for tags
      if (searchQuery.length >= 2) {
        setIsLoading(true);
        try {
          const response = await fetch(`/api/competitor-tags?search=${encodeURIComponent(searchQuery)}&limit=10`);
          const data = await response.json();
          if (data.success) {
            setAvailableTags(data.data);
          }
        } catch (error) {
          console.error('Error searching competitor tags:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    const debounceTimer = setTimeout(loadTags, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setSearchQuery('');
        setNewTagName('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTagSelect = async (tagName: string) => {
    if (!value.includes(tagName)) {
      const newTags = [...value, tagName];
      onChange(newTags);
      
      // Record usage
      try {
        await fetch('/api/competitor-tags', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: tagName })
        });
      } catch (error) {
        console.error('Error recording tag usage:', error);
      }
    }
    
    setSearchQuery('');
    setNewTagName('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleTagRemove = (tagToRemove: string) => {
    const newTags = value.filter(tag => tag !== tagToRemove);
    onChange(newTags);
  };

  const handleCreateNewTag = async () => {
    const trimmedName = newTagName.trim();
    if (trimmedName && !value.includes(trimmedName)) {
      try {
        const response = await fetch('/api/competitor-tags', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmedName })
        });
        
        if (response.ok) {
          await handleTagSelect(trimmedName);
        }
      } catch (error) {
        console.error('Error creating new tag:', error);
      }
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (newTagName.trim()) {
        handleCreateNewTag();
      } else if (availableTags.length > 0) {
        // Select first available tag
        const firstTag = availableTags[0];
        if (!value.includes(firstTag.name)) {
          handleTagSelect(firstTag.name);
        }
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSearchQuery('');
      setNewTagName('');
    } else if (e.key === 'Backspace' && !searchQuery && value.length > 0) {
      // Remove last tag if input is empty
      handleTagRemove(value[value.length - 1]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchQuery(inputValue);
    setNewTagName(inputValue);
    setShowSuggestions(true);
  };

  const filteredTags = availableTags.filter(tag => 
    !value.includes(tag.name) && 
    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const showCreateOption = newTagName.trim() && 
    !availableTags.some(tag => tag.name.toLowerCase() === newTagName.toLowerCase()) &&
    !value.includes(newTagName.trim());

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <Label className="text-slate-700 font-medium">
          {label}
        </Label>
      )}
      
      <div className="relative" ref={dropdownRef}>
        {/* Selected Tags Display */}
        <div className="min-h-[40px] border border-slate-300 rounded-md p-2 focus-within:border-amber-500 bg-white">
          <div className="flex flex-wrap gap-1 items-center">
            {value.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {tag}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleTagRemove(tag)}
                    className="ml-1 hover:text-red-600"
                  >
                    <X size={12} />
                  </button>
                )}
              </Badge>
            ))}
            
            {!disabled && (
              <Input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onFocus={() => setShowSuggestions(true)}
                placeholder={value.length === 0 ? placeholder : "Přidat další značku..."}
                className="border-0 p-0 h-6 flex-1 min-w-[150px] focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            )}
          </div>
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestions && !disabled && (
          <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="p-3 text-sm text-slate-500 flex items-center gap-2">
                <Search size={16} className="animate-spin" />
                Vyhledávání...
              </div>
            ) : (
              <>
                {/* Existing Tags */}
                {filteredTags.length > 0 && (
                  <div className="p-2">
                    <div className="text-xs text-slate-500 mb-2 px-2">Existující značky:</div>
                    {filteredTags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => handleTagSelect(tag.name)}
                        className="w-full text-left p-2 hover:bg-slate-100 rounded-md text-sm flex items-center justify-between"
                      >
                        <span>{tag.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {tag.usage_count}×
                        </Badge>
                      </button>
                    ))}
                  </div>
                )}

                {/* Create New Tag Option */}
                {showCreateOption && (
                  <div className="border-t border-slate-100 p-2">
                    <button
                      type="button"
                      onClick={handleCreateNewTag}
                      className="w-full text-left p-2 hover:bg-green-50 rounded-md text-sm flex items-center gap-2 text-green-700"
                    >
                      <Plus size={16} />
                      Přidat novou značku: "{newTagName.trim()}"
                    </button>
                  </div>
                )}

                {/* No Results */}
                {filteredTags.length === 0 && !showCreateOption && searchQuery && (
                  <div className="p-3 text-sm text-slate-500">
                    Žádné značky nenalezeny
                  </div>
                )}

                {/* Initial State */}
                {filteredTags.length === 0 && !searchQuery && (
                  <div className="p-3 text-sm text-slate-500">
                    Začněte psát pro vyhledání značek nebo přidání nové
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {value.length > 0 && (
        <div className="text-xs text-slate-500">
          Vybráno {value.length} značek
        </div>
      )}
    </div>
  );
};

export default CompetitorTagsSelector; 